/**
 * useFieldScoutRecording — per-pest-per-point mobile recording state hook.
 *
 * Owns a FieldScoutDraft in db.fieldScoutDrafts (Dexie v12), with GPS/
 * geofence/accuracy-gating patterns mirrored from useCompactScouting.
 *
 * Track: field_scout_recording_mobile / Slice 2 / Task S2-7.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { db, type Orchard, type Pest } from '../../../db';
import { saveMedia } from '../../../services/mediaService';
import { reducePhoto } from '../../../services/photoReduce';
import { syncPendingFieldScout } from '../../../services/fieldScoutSyncService';
import { getSignedInTenantId } from '../../../services/signedInTenant';
import { tallyThresholdInForce, examinedCount } from '../../../scouting/recording';
import { normalizeMeasurementType } from '../../../scouting/pestConfig';
import { generateUUID } from '../../../utils';

// ──────────────────────────────────────────────────────────────────────────────
//  Types
// ──────────────────────────────────────────────────────────────────────────────

/** Snapshot stored on the undo stack — only the mutable draft slices. */
interface UndoSnapshot {
  infested: Record<string, number[]>;
  levels: Record<string, 0 | 1 | 2 | 3 | 4>;
  counts: Record<string, number>;
  photos: Record<number, number[]>;
  activePestId: string;
}

// ──────────────────────────────────────────────────────────────────────────────
//  Hook
// ──────────────────────────────────────────────────────────────────────────────

export const useFieldScoutRecording = (
  orchardId: string,
  scouterName: string,
  orchardName: string,
  pests: Pest[],
) => {
  // ── Mutable draft state ────────────────────────────────────────────────────
  const [draftId, setDraftId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const [currentPoint, setCurrentPoint] = useState(0);
  const [maxExamined, setMaxExamined] = useState(0);
  const [activePestId, setActivePestId] = useState('');
  const [mode, setModeState] = useState<'solo' | 'all'>('solo');
  const [infested, setInfested] = useState<Record<string, number[]>>({});
  const [tallyInForce, setTallyInForce] = useState<Record<string, 'any' | '2+' | '3+'>>({});
  const [levels, setLevels] = useState<Record<string, 0 | 1 | 2 | 3 | 4>>({});
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [photos, setPhotos] = useState<Record<number, number[]>>({});
  const [pointCoords, setPointCoords] = useState<Record<number, { latitude: number; longitude: number; accuracy: number; timestamp: string }>>({});

  // ── GPS / geofence state (mirrors useCompactScouting) ─────────────────────
  const [detectedOrchardName, setDetectedOrchardName] = useState<string>('Unknown');
  const [currentAccuracy, setCurrentAccuracy] = useState<number>(Infinity);
  const [isAccuracyPoor, setIsAccuracyPoor] = useState(true);
  const [isManualOverride, setIsManualOverride] = useState(false);
  const [availableOrchards, setAvailableOrchards] = useState<Orchard[]>([]);
  const lastPosition = useRef<{ lat: number; lon: number; accuracy: number; hardwareTimestamp: number } | null>(null);

  // ── Undo stack (in-memory only) ────────────────────────────────────────────
  const undoStack = useRef<UndoSnapshot[]>([]);

  // ──────────────────────────────────────────────────────────────────────────
  //  Spatial lookup (identical copy from useCompactScouting)
  // ──────────────────────────────────────────────────────────────────────────
  const findOrchardAt = useCallback((lat: number, lon: number) => {
    if (availableOrchards.length === 0) return 'Unknown';

    const isPointInPolygon = (point: [number, number], polygon: number[][][]) => {
      if (!polygon || !polygon[0]) return false;
      const x = point[0], y = point[1];
      let inside = false;
      const vs = polygon[0];
      for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
        const xi = vs[i][0], yi = vs[i][1];
        const xj = vs[j][0], yj = vs[j][1];
        const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
      }
      return inside;
    };

    for (const orchard of availableOrchards) {
      if (
        orchard.boundary &&
        isPointInPolygon([lon, lat], orchard.boundary.coordinates)
      ) {
        return orchard.orchardName;
      }
    }
    return 'Unknown';
  }, [availableOrchards]);

  // ──────────────────────────────────────────────────────────────────────────
  //  GPS watcher (mirrors useCompactScouting)
  // ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const geo = navigator.geolocation;
    if (!geo) return;

    const watchId = geo.watchPosition(
      (pos) => {
        lastPosition.current = {
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          hardwareTimestamp: pos.timestamp,
        };
        setCurrentAccuracy(pos.coords.accuracy);
        setIsAccuracyPoor(pos.coords.accuracy > 15);
        const name = findOrchardAt(pos.coords.latitude, pos.coords.longitude);
        setDetectedOrchardName(name);
      },
      (err) => console.warn('GPS Watcher error:', err),
      { enableHighAccuracy: true, timeout: 60000, maximumAge: 0 },
    );

    return () => geo.clearWatch(watchId);
  }, [findOrchardAt]);

  // Reset override when scout identity changes
  useEffect(() => {
    setIsAccuracyPoor(true);
    setIsManualOverride(false);
  }, [orchardId, scouterName]);

  // Load orchards for spatial lookup
  useEffect(() => {
    db.orchards.toArray().then(setAvailableOrchards);
  }, []);

  // ──────────────────────────────────────────────────────────────────────────
  //  Draft load/create on mount
  // ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true;

    const loadDraft = async () => {
      if (!orchardId || !scouterName) {
        if (isMounted) setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const existing = await db.fieldScoutDrafts
          .where('[orchardId+scouterName+isComplete]')
          .equals([orchardId, scouterName, 0])
          .first();

        if (existing && isMounted) {
          setDraftId(existing.id!);
          setCurrentPoint(existing.currentPoint);
          setMaxExamined(existing.maxExamined);
          setActivePestId(existing.activePestId);
          setModeState(existing.mode);
          setInfested(existing.infested);
          setTallyInForce(existing.tallyInForce as Record<string, 'any' | '2+' | '3+'>);
          setLevels(existing.levels as Record<string, 0 | 1 | 2 | 3 | 4>);
          setCounts(existing.counts);
          setPhotos(existing.photos);
          setPointCoords(existing.pointCoords ?? {});
        } else if (isMounted) {
          // Derive default activePestId: first incidence pest (or '')
          const firstIncidence =
            pests.find((p) => normalizeMeasurementType(p.measurementType) === 'incidence')?.id ?? '';

          const id = await db.fieldScoutDrafts.add({
            orchardId,
            orchardName,
            scouterName,
            currentPoint: 0,
            maxExamined: 0,
            activePestId: firstIncidence,
            mode: 'solo',
            infested: {},
            tallyInForce: {},
            levels: {},
            counts: {},
            photos: {},
            pointCoords: {},
            startTime: new Date().toISOString(),
            lastUpdated: new Date().toISOString(),
            isComplete: 0,
          });

          setDraftId(id as number);
          setCurrentPoint(0);
          setMaxExamined(0);
          setActivePestId(firstIncidence);
          setModeState('solo');
          setInfested({});
          setTallyInForce({});
          setLevels({});
          setCounts({});
          setPhotos({});
          setPointCoords({});
        }
      } catch (err) {
        console.error('Failed to load field scout draft:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadDraft();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orchardId, scouterName, orchardName]);

  // ──────────────────────────────────────────────────────────────────────────
  //  Autosave to Dexie on state change (mirrors useCompactScouting pattern)
  // ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (draftId !== null) {
      db.fieldScoutDrafts
        .update(draftId, {
          currentPoint,
          maxExamined,
          activePestId,
          mode,
          infested,
          tallyInForce,
          levels,
          counts,
          photos,
          pointCoords,
          lastUpdated: new Date().toISOString(),
        })
        .catch((err) => console.error('Failed to autosave field scout draft:', err));
    }
  }, [draftId, currentPoint, maxExamined, activePestId, mode, infested, tallyInForce, levels, counts, photos, pointCoords]);

  // ──────────────────────────────────────────────────────────────────────────
  //  Internal helpers
  // ──────────────────────────────────────────────────────────────────────────

  const snapshot = useCallback((): UndoSnapshot => ({
    infested: Object.fromEntries(Object.entries(infested).map(([k, v]) => [k, [...v]])),
    levels: { ...levels },
    counts: { ...counts },
    photos: Object.fromEntries(Object.entries(photos).map(([k, v]) => [k, [...v]])),
    activePestId,
  }), [infested, levels, counts, photos, activePestId]);

  const pushUndo = useCallback((snap: UndoSnapshot) => {
    undoStack.current = [...undoStack.current.slice(-19), snap]; // keep last 20
  }, []);

  // ──────────────────────────────────────────────────────────────────────────
  //  Public actions
  // ──────────────────────────────────────────────────────────────────────────

  /** Toggle incidence for pestId at currentPoint; stamp tallyInForce; push undo. */
  const toggleIncidence = useCallback((pestId: string) => {
    pushUndo(snapshot());

    const pest = pests.find((p) => p.id === pestId);
    const tally = pest ? tallyThresholdInForce(pest) : 'any';

    setTallyInForce((prev) => ({ ...prev, [pestId]: tally }));
    setActivePestId(pestId);
    setInfested((prev) => {
      const current = prev[pestId] ?? [];
      const idx = current.indexOf(currentPoint);
      const next = idx >= 0 ? current.filter((p) => p !== currentPoint) : [...current, currentPoint];
      return { ...prev, [pestId]: next };
    });
  }, [currentPoint, pests, snapshot, pushUndo]);

  /** Raise level by 1, clamped at 4; push undo. */
  const raiseLevel = useCallback((pestId: string) => {
    pushUndo(snapshot());
    setLevels((prev) => {
      const current = (prev[pestId] ?? 0) as number;
      const next = Math.min(4, current + 1) as 0 | 1 | 2 | 3 | 4;
      return { ...prev, [pestId]: next };
    });
  }, [snapshot, pushUndo]);

  /** Lower level by 1, clamped at 0; push undo. */
  const lowerLevel = useCallback((pestId: string) => {
    pushUndo(snapshot());
    setLevels((prev) => {
      const current = (prev[pestId] ?? 0) as number;
      const next = Math.max(0, current - 1) as 0 | 1 | 2 | 3 | 4;
      return { ...prev, [pestId]: next };
    });
  }, [snapshot, pushUndo]);

  /** Increment count, no upper bound; push undo. */
  const incCount = useCallback((pestId: string) => {
    pushUndo(snapshot());
    setCounts((prev) => ({ ...prev, [pestId]: (prev[pestId] ?? 0) + 1 }));
  }, [snapshot, pushUndo]);

  /** Decrement count, clamped at 0; push undo. */
  const decCount = useCallback((pestId: string) => {
    pushUndo(snapshot());
    setCounts((prev) => ({ ...prev, [pestId]: Math.max(0, (prev[pestId] ?? 0) - 1) }));
  }, [snapshot, pushUndo]);

  /** Move forward one point, bumping maxExamined when needed. */
  const next = useCallback(() => {
    setCurrentPoint((prev) => {
      const next = Math.min(99, prev + 1);
      setMaxExamined((me) => Math.max(me, next));
      return next;
    });
  }, []);

  /** Idempotently record the latest GPS fix for the current point (first fix wins). */
  const stampCurrentPoint = useCallback(() => {
    const fix = lastPosition.current;
    if (!fix) return;
    setPointCoords((prev) => {
      if (prev[currentPoint]) return prev; // first fix wins — never overwrite on revisit
      return {
        ...prev,
        [currentPoint]: {
          latitude: fix.lat,
          longitude: fix.lon,
          accuracy: fix.accuracy,
          timestamp: new Date().toISOString(),
        },
      };
    });
  }, [currentPoint]);

  /** Move back one point (never below 0). */
  const prev = useCallback(() => {
    setCurrentPoint((p) => Math.max(0, p - 1));
  }, []);

  /** Jump to point i, only if i <= maxExamined. */
  const pickPoint = useCallback((i: number) => {
    setCurrentPoint((cp) => {
      // We need to read maxExamined at call time — use functional update trick
      // by reading the ref value stored from the effect.
      // Since maxExamined is a React state value, capture via the closure.
      return i <= maxExamined ? i : cp;
    });
  }, [maxExamined]);

  /** Clear all pest infested marks for currentPoint; push undo. */
  const clearPoint = useCallback(() => {
    pushUndo(snapshot());
    setInfested((prev) => {
      const updated: Record<string, number[]> = {};
      for (const [pestId, points] of Object.entries(prev)) {
        updated[pestId] = points.filter((p) => p !== currentPoint);
      }
      return updated;
    });
  }, [currentPoint, snapshot, pushUndo]);

  /** Save a photo for currentPoint. */
  const capturePhoto = useCallback(async (file: File) => {
    const mediaId = await saveMedia(0, await reducePhoto(file));
    setPhotos((prev) => ({
      ...prev,
      [currentPoint]: [...(prev[currentPoint] ?? []), mediaId],
    }));
  }, [currentPoint]);

  /** Restore the last undo snapshot (no-op when stack is empty). */
  const undo = useCallback(() => {
    if (undoStack.current.length === 0) return;
    const top = undoStack.current[undoStack.current.length - 1];
    undoStack.current = undoStack.current.slice(0, -1);
    setInfested(top.infested);
    setLevels(top.levels);
    setCounts(top.counts);
    setPhotos(top.photos);
    setActivePestId(top.activePestId);
  }, []);

  const setActivePest = useCallback((pestId: string) => {
    setActivePestId(pestId);
  }, []);

  const setMode = useCallback((m: 'solo' | 'all') => {
    setModeState(m);
  }, []);

  // ──────────────────────────────────────────────────────────────────────────
  //  Submit — build payload, enqueue, mark complete, reset, trigger sync
  // ──────────────────────────────────────────────────────────────────────────

  const submit = useCallback(async (): Promise<string> => {
    const uuid = generateUUID();
    const timestamp = new Date().toISOString();

    // Build incidence rows: for each pestId → each infested point
    const incidenceRows: Array<{
      pestId: string;
      inspectionPointId: number;
      infested: true;
      tallyThresholdInForce: string;
      timestamp: string;
    }> = [];

    for (const [pestId, points] of Object.entries(infested)) {
      for (const pointId of points) {
        incidenceRows.push({
          pestId,
          inspectionPointId: pointId,
          infested: true,
          tallyThresholdInForce: tallyInForce[pestId] ?? 'any',
          timestamp,
        });
      }
    }

    // Build level rows
    const levelRows = Object.entries(levels)
      .filter(([, v]) => v > 0)
      .map(([pestId, v]) => ({ pestId, valueSeverity: v, timestamp }));

    // Build count rows
    const countRows = Object.entries(counts)
      .filter(([, v]) => v > 0)
      .map(([pestId, v]) => ({ pestId, valueCount: v, timestamp }));

    // Photo manifest: references (mediaIds) grouped by inspection point. The blobs
    // stay in db.media; the sync drainer resolves them to base64 at upload time.
    const photoManifest = Object.entries(photos)
      .filter(([, ids]) => Array.isArray(ids) && ids.length > 0)
      .map(([pointId, ids]) => ({ inspectionPointId: Number(pointId), mediaIds: ids }));

    // Per-point GPS coordinates (issue #392) — one row per stamped inspection point.
    const pointRows = Object.entries(pointCoords).map(([pointId, c]) => ({
      inspectionPointId: Number(pointId),
      latitude:  c.latitude,
      longitude: c.longitude,
      accuracy:  c.accuracy,
      timestamp: c.timestamp,
    }));

    const payload = {
      blockId: orchardName,
      orchardName,
      uuid,
      date: timestamp,
      scouterName,
      examinedCount: examinedCount(maxExamined),
      totalInspectionPoints: 100,
      incidence: incidenceRows,
      level: levelRows,
      count: countRows,
      photos: photoManifest,
      points: pointRows,
    };

    // Collect all photo mediaIds so we can re-link them to the stable uuid
    const allMediaIds = Object.values(photos).flat();

    // Enqueue for sync
    await db.fieldScoutQueue.add({
      uuid,
      payload,
      syncStatus: 'pending',
      createdAt: timestamp,
      tenantId: getSignedInTenantId() ?? undefined,
    });

    // Re-link media rows to the queue item's uuid via sessionId convention:
    // mediaService stores sessionId as 0 (temp); we update to the queue row id
    // so they are not orphaned. The server handles photos server-side via the
    // payload's incidence rows; we just need the media not to be pruned locally.
    if (allMediaIds.length > 0) {
      const queueItem = await db.fieldScoutQueue
        .where('uuid')
        .equals(uuid)
        .first();
      if (queueItem?.id !== undefined) {
        await db.media
          .where('id')
          .anyOf(allMediaIds)
          .modify({ sessionId: queueItem.id });
      }
    }

    // Mark the current draft complete
    if (draftId !== null) {
      await db.fieldScoutDrafts.update(draftId, {
        isComplete: 1,
        lastUpdated: timestamp,
        queueUuid: uuid,
      });
    }

    // Reset to a fresh draft (mirrors reinitializeSession in useCompactScouting)
    const firstIncidence =
      pests.find((p) => normalizeMeasurementType(p.measurementType) === 'incidence')?.id ?? '';

    const newDraftId = await db.fieldScoutDrafts.add({
      orchardId,
      orchardName,
      scouterName,
      currentPoint: 0,
      maxExamined: 0,
      activePestId: firstIncidence,
      mode: 'solo',
      infested: {},
      tallyInForce: {},
      levels: {},
      counts: {},
      photos: {},
      pointCoords: {},
      startTime: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      isComplete: 0,
    });

    setDraftId(newDraftId as number);
    setCurrentPoint(0);
    setMaxExamined(0);
    setActivePestId(firstIncidence);
    setModeState('solo');
    setInfested({});
    setTallyInForce({});
    setLevels({});
    setCounts({});
    setPhotos({});
    setPointCoords({});
    undoStack.current = [];

    // Trigger sync — fire and forget (offline-safe)
    syncPendingFieldScout().catch((err) =>
      console.error('[FieldScoutRecording] sync trigger failed:', err),
    );

    return uuid;
  }, [
    orchardId,
    orchardName,
    scouterName,
    draftId,
    maxExamined,
    infested,
    tallyInForce,
    levels,
    counts,
    photos,
    pointCoords,
    pests,
  ]);

  // Discard the in-progress draft (abort the session without submitting).
  const discard = useCallback(async () => {
    await db.fieldScoutDrafts
      .where('[orchardId+scouterName+isComplete]')
      .equals([orchardId, scouterName, 0])
      .delete();
  }, [orchardId, scouterName]);

  // ──────────────────────────────────────────────────────────────────────────
  //  Return
  // ──────────────────────────────────────────────────────────────────────────
  return {
    // State
    loading,
    currentPoint,
    maxExamined,
    activePestId,
    mode,
    infested,
    tallyInForce,
    levels,
    counts,
    photos,
    pointCoords,
    // GPS / geofence
    detectedOrchardName,
    currentAccuracy,
    isAccuracyPoor,
    isManualOverride,
    triggerManualOverride: () => setIsManualOverride(true),
    // Actions
    toggleIncidence,
    raiseLevel,
    lowerLevel,
    incCount,
    decCount,
    next,
    stampCurrentPoint,
    prev,
    pickPoint,
    clearPoint,
    capturePhoto,
    undo,
    setActivePest,
    setMode,
    submit,
    discard,
  };
};

export default useFieldScoutRecording;
